/**
 * Journey — CLIENT track (Phase 1 · Front-door build · Phase-2 Slice 3).
 *
 * OWNER CORRECTION (memory project_avbp_conversation_visibility_model,
 * 2026-05-17): MONEY IS CLIENT-VISIBLE. The client's Journey shows a real
 * DATED timeline (quote → payments → report) WITH a running outstanding
 * balance, per-project paid / due figures, exactly WHAT TO PAY NEXT to unlock
 * the next stage, AND a cross-project summary (total paid / total due / next
 * payment across all their projects).
 *
 * Slice 3 swaps the DEMO SEED for LIVE, client-scoped data: the figures now
 * come from the signed-in client's OWN projects' authoritative
 * `gateState.moneyPaid` (via lib/clientProjects → the locked engagementTemplate
 * pricing math). A client sees ONLY their own projects' money — never another
 * client's. With the demo seed off + no live data, a neutral empty state.
 *
 * Still client-HIDDEN (unchanged): the AI extraction / Review Queue / internal
 * notes / workload mechanics — governed by the conversation spine filter.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { visibilityFor } from '../lib/roles';
import { Box, Btn3, Card, EmptyNote, StageRail, Tag } from '../components/ui';
import { ListLoading } from '../components/ListState';
import { useToast } from '../components/Toast';
import { listenClientProjects } from '../lib/clientProjects';
import {
  inr,
  railOf,
  templateOf,
  clientProjectMoney,
  clientMoneySummary,
  type DemoProject,
} from '../lib/trackDemo';

export default function Journey() {
  const { role, clientId } = useAuth();
  const vis = visibilityFor(role);
  const { toast } = useToast();

  const [projects, setProjects] = useState<DemoProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [activePid, setActivePid] = useState<string | null>(null);

  useEffect(() => {
    const unsub = listenClientProjects(
      clientId,
      (state) => {
        setProjects(state.projects);
        setActivePid((cur) => cur ?? state.projects[0]?.pid ?? null);
      },
      {
        onErr: () => toast('Could not load your journey (check connection)'),
        onReady: () => setLoading(false),
      },
    );
    return unsub;
  }, [clientId, toast]);

  const p = useMemo(
    () => projects.find((x) => x.pid === activePid) ?? projects[0] ?? null,
    [projects, activePid],
  );

  if (loading) {
    return (
      <Box title="Your journey">
        <ListLoading label="Loading your journey…" rows={3} />
      </Box>
    );
  }

  if (!p) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Box title="Your journey">
          <EmptyNote title="Nothing to show yet">
            Your journey, payments and report timeline will appear here once
            your engagement is set up.
          </EmptyNote>
        </Box>
      </div>
    );
  }

  const tpl = templateOf(p);
  const money = clientProjectMoney(p);
  // Cross-project summary across THIS client's own projects only.
  const summary = clientMoneySummary(projects);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {projects.length > 1 && (
        <Box title="Choose a project" note="You have more than one engagement">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {projects.map((x) => (
              <button
                key={x.pid}
                type="button"
                className={`btn3 ${x.pid === p.pid ? 'ink' : 'gh'}`}
                onClick={() => setActivePid(x.pid)}
              >
                {x.vp}
              </button>
            ))}
          </div>
        </Box>
      )}

      <Box title="Your journey" note={tpl.displayName}>
        <StageRail stages={railOf(p)} />
      </Box>

      {/* ── Cross-project money summary (owner-locked: client sees this) ── */}
      <Box
        title="Your payments — all projects"
        note="What you've paid, what's outstanding, and what's next"
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px,1fr))',
            gap: 12,
          }}
        >
          <Fig k="Across" v={`${summary.projectCount} project(s)`} />
          <Fig k="Total paid" v={inr(summary.totalPaidINR)} tone="good" />
          <Fig k="Total outstanding" v={inr(summary.totalDueINR)} tone="warn" />
        </div>
        {summary.nextAcrossProjects && (
          <Card flat className="row-card" style={{ marginTop: 14 }}>
            <div style={{ fontSize: 13.5, color: 'var(--ink-soft)' }}>
              <b style={{ color: 'var(--ink)' }}>To unlock your next step:</b>{' '}
              pay <b>{inr(summary.nextAcrossProjects.amountINR)}</b> on{' '}
              {summary.nextAcrossProjects.displayName}.
              <br />
              <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                {summary.nextAcrossProjects.unlocks}
              </span>
            </div>
          </Card>
        )}
      </Box>

      {/* ── This project: paid / due + what to pay next ── */}
      <Box
        title={`This project · ${p.vp}`}
        note={`${inr(money.paidINR)} paid · ${inr(money.dueINR)} outstanding`}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px,1fr))',
            gap: 12,
          }}
        >
          <Fig k="Engagement value" v={inr(money.valueINR)} />
          <Fig k="Paid" v={inr(money.paidINR)} tone="good" />
          <Fig k="Outstanding" v={inr(money.dueINR)} tone="warn" />
        </div>
        {money.nextPayment ? (
          <Card flat className="row-card" style={{ marginTop: 14 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <b>{inr(money.nextPayment.amountINR)}</b>
              <span style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
                {money.nextPayment.label}
              </span>
              <Tag kind="warn">pay next</Tag>
              <span
                style={{
                  flexBasis: '100%',
                  fontSize: 12,
                  color: 'var(--ink-mute)',
                }}
              >
                {money.nextPayment.unlocks}
              </span>
            </div>
          </Card>
        ) : (
          <p style={{ fontSize: 13, color: 'var(--good)', marginTop: 12 }}>
            You're fully paid up for the current stage — nothing due right now.
          </p>
        )}
      </Box>

      {/* ── Dated journey timeline with running balance ── */}
      <Box
        title="What's happened so far"
        note="Quotes, payments and reports — your running balance"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {money.timeline.map((ev, i) => (
            <Card key={`${ev.date}-${i}`} flat>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background:
                      ev.kind === 'payment'
                        ? 'var(--good)'
                        : 'var(--accent)',
                    flex: 'none',
                  }}
                />
                <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                  {ev.date}
                </span>
                <span style={{ fontSize: 13.5, color: 'var(--ink-soft)' }}>
                  {ev.label}
                </span>
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: 12,
                    color: 'var(--ink-mute)',
                  }}
                >
                  balance {inr(ev.balanceAfterINR)}
                </span>
              </div>
            </Card>
          ))}
        </div>
        <div style={{ marginTop: 12 }}>
          <Link to={`/conversation/${p.pid}`}>
            <Btn3 intent="ink">Open conversation</Btn3>
          </Link>
        </div>
      </Box>

      {/* Spine assertion — money is now correctly client-visible; the
          AI/review/internal layer remains hidden (unchanged). */}
      {!vis.seesReviewLayer && (
        <p style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
          You see your payments, journey and reports. The team's internal
          working notes and review workspace are not part of your view.
        </p>
      )}
    </div>
  );
}

function Fig({
  k,
  v,
  tone,
}: {
  k: string;
  v: string;
  tone?: 'good' | 'warn';
}) {
  return (
    <div
      style={{
        border: '1px solid var(--rule)',
        borderRadius: 'var(--r-sm)',
        padding: '12px 14px',
      }}
    >
      <div style={{ fontSize: 12, color: 'var(--ink-mute)', marginBottom: 4 }}>
        {k}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 21,
          fontWeight: 600,
          color:
            tone === 'good'
              ? 'var(--good)'
              : tone === 'warn'
                ? 'var(--warn)'
                : 'var(--ink)',
        }}
      >
        {v}
      </div>
    </div>
  );
}
